import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { alpha, Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router';

import Logger from 'browser-logging/src/browser/Logger';

import { BASE } from 'common/src/routes/BaseRoutes';
import { DevFeatures } from 'common/src/types/enums/ProductFeatures.enum';

import { forgotPasswordService } from 'portal/src/api/authService';
import useAuth from 'portal/src/auth/useAuth';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import usePageTitle from 'portal/src/navigation/usePageTitle';

import FormTextInput from '../../components/common/forms/FormTextInput';
import useSnackbarHandler from '../../utils/Snackbar/useSnackbarHandler';

export default function ForgotPassword() {
  usePageTitle('Forgot Password');

  const snackbarHandler = useSnackbarHandler();
  const { featureChecker } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const sendResetPasswordEmail = async (email) => {
    try {
      await forgotPasswordService(
        { email },
        featureChecker.devFeatureEnabled(DevFeatures.WorkOsLogin),
      );
      setEmail(email);
    } catch (err) {
      Logger.captureError(err as Error);
      snackbarHandler.error(MESSAGES.PASSWORD.FORGOT_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const submit = ({ email }) => {
    setLoading(true);
    sendResetPasswordEmail(email);
  };

  return !email ? (
    <Box>
      <Stack spacing={2} alignItems="center" marginTop={4}>
        <Stack
          width={'4.5rem'}
          height={'4.5rem'}
          justifyContent="center"
          alignItems="center"
          position="relative"
          sx={{
            borderRadius: '50%',
            backgroundColor: (theme) =>
              alpha(theme.palette.secondary.main, 0.1),
          }}
        >
          <Stack
            width={'3rem'}
            height={'3rem'}
            alignItems="center"
            justifyContent="center"
            sx={{
              borderRadius: '50%',
              backgroundColor: (theme) =>
                alpha(theme.palette.secondary.main, 0.1),
            }}
          >
            <LockOpenIcon fontSize="large" color="secondary" />
          </Stack>
        </Stack>
        <Typography variant="h3" textAlign={'center'}>
          Forgot password?
        </Typography>
        <Typography variant="body1" textAlign={'center'}>
          No worries, we got you! Enter your email address and we will send
          reset instructions.
        </Typography>
      </Stack>
      <form autoComplete="off" onSubmit={handleSubmit(submit)}>
        <Stack spacing={2} alignItems="center" marginTop={4}>
          <Box
            sx={{
              width: '20rem',
            }}
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => {
                delete field.ref;

                return (
                  <FormTextInput
                    name="email"
                    type="email"
                    placeholder="Email"
                    label="Email"
                    error={!!errors.email}
                    required
                    {...field}
                  />
                );
              }}
              rules={{
                required: true,
              }}
            />
          </Box>
          <Button
            disabled={loading}
            type="submit"
            variant="contained"
            disableElevation
          >
            Reset Password
          </Button>
          <Button
            disabled={loading}
            variant="text"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={BASE.AUTH.LOGIN}
          >
            Back to login
          </Button>
        </Stack>
      </form>
    </Box>
  ) : (
    <Stack
      alignItems="center"
      justifyContent="center"
      direction="column"
      height="100vh"
    >
      <Stack spacing={2} alignItems="center" width="20rem">
        <Typography>
          If there is an account associated with {email}, you will receive an
          email with instructions to reset your password. If you don&apos;t
          receive an email, please check your spam folder.
        </Typography>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          component={Link}
          to={BASE.AUTH.LOGIN}
        >
          Back to login
        </Button>
      </Stack>
    </Stack>
  );
}
